"use client";

import { useState, useEffect, useRef } from "react";

import Icon from "@/components/ui/AppIcon";

interface Testimonial {
  id: number;
  name: string;
  location: string;
  image: string;
  alt: string;
  quote: string;
  service: string;
  rating: number;
}

const testimonials: Testimonial[] = [
{
  id: 1,
  name: "Achiro Grace",
  location: "Gulu Town",
  image: "https://img.rocket.new/generatedImages/rocket_gen_img_149449c2d-1769488327095.png",
  alt: "Achiro Grace, a patient from Gulu Town smiling warmly",
  quote: "The Gyn Consult gave me hope when I thought I couldn't conceive. After 3 years of trying, their fertility treatment worked. I'm now a proud mother!",
  service: "Fertility Treatment",
  rating: 5
},
{
  id: 2,
  name: "Akello Patricia",
  location: "Layibi Division",
  image: "https://img.rocket.new/generatedImages/rocket_gen_img_18526be56-1770295488662.png",
  alt: "Akello Patricia, a patient from Layibi Division with a confident expression",
  quote: "My PCOS was making life unbearable. The specialist here understood my struggles and created a treatment plan that actually worked. I feel like myself again.",
  service: "PCOS Management",
  rating: 5
},
{
  id: 3,
  name: "Lamwaka Sarah",
  location: "Pece Division",
  image: "https://img.rocket.new/generatedImages/rocket_gen_img_105b2853b-1771707267561.png",
  alt: "Lamwaka Sarah, a patient from Pece Division looking grateful and happy",
  quote: "The antenatal care I received was exceptional. Every visit made me feel safe and prepared. My delivery was smooth thanks to their guidance.",
  service: "Antenatal Care",
  rating: 5
},
{
  id: 4,
  name: "Atim Florence",
  location: "Bardege Division",
  image: "https://img.rocket.new/generatedImages/rocket_gen_img_18e4092e6-1767671569788.png",
  alt: "Atim Florence, a patient from Bardege Division with a relieved smile",
  quote: "Living with endometriosis was painful for years. The team here didn't just treat my symptoms—they gave me my life back. Forever grateful.",
  service: "Endometriosis Treatment",
  rating: 5
},
{
  id: 5,
  name: "Okello Christine",
  location: "Laroo Division",
  image: "https://img.rocket.new/generatedImages/rocket_gen_img_1bb9dcdf3-1763294738941.png",
  alt: "Okello Christine, a patient from Laroo Division looking content and healthy",
  quote: "The ultrasound services are world-class. Clear explanations, modern equipment, and caring staff. I trust them completely with my health.",
  service: "Ultrasound Services",
  rating: 5
},
{
  id: 6,
  name: "Ojok Mary",
  location: "Pece Laroo",
  image: "https://img.rocket.new/generatedImages/rocket_gen_img_18526be56-1770295488662.png",
  alt: "Ojok Mary, a patient from Pece Laroo with a bright, thankful expression",
  quote: "Family planning counseling here changed my perspective. They respect your choices and provide options that fit your life. Highly recommend!",
  service: "Family Planning",
  rating: 5
}];


export default function TestimonialsCarousel() {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isAutoPlaying, setIsAutoPlaying] = useState(true);
  const [isVisible, setIsVisible] = useState(false);
  const sectionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setIsVisible(true);
          }
        });
      },
      { threshold: 0.2 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (!isAutoPlaying) return;

    const interval = setInterval(() => {
      setCurrentIndex((prev) => (prev + 1) % testimonials.length);
    }, 5000);

    return () => clearInterval(interval);
  }, [isAutoPlaying]);

  const goToSlide = (index: number) => {
    setCurrentIndex(index);
    setIsAutoPlaying(false);
  };

  const goToPrevious = () => {
    setCurrentIndex((prev) => (prev - 1 + testimonials.length) % testimonials.length);
    setIsAutoPlaying(false);
  };

  const goToNext = () => {
    setCurrentIndex((prev) => (prev + 1) % testimonials.length);
    setIsAutoPlaying(false);
  };

  const currentTestimonial = testimonials[currentIndex];

  return (
    <section className="relative py-16 sm:py-20 md:py-24 px-4 sm:px-6 overflow-hidden gpu-accelerated" id="testimonials" ref={sectionRef}>
      {/* Background */}
      <div className="absolute inset-0 bg-gradient-to-br from-royal-purple via-deep-purple to-mid-purple dark:from-navy-900 dark:via-navy-800 dark:to-deep-purple">
        <div className="absolute inset-0 opacity-20">
          <div className="absolute top-20 right-10 w-72 h-72 bg-pink-brand dark:bg-amber-500 rounded-full mix-blend-multiply filter blur-3xl animate-float-a" />
          <div className="absolute bottom-20 left-20 w-96 h-96 bg-pink-brand dark:bg-amber-600 rounded-full mix-blend-multiply filter blur-3xl animate-float-b" />
        </div>
      </div>

      <div className="relative max-w-6xl mx-auto">
        {/* Section Header */}
        <div className={`text-center mb-12 sm:mb-14 md:mb-16 transition-all duration-1000 gpu-accelerated ${
        isVisible ? "opacity-100 translate-y-0" : "opacity-0 -translate-y-8"}`
        }>
          <div className="inline-flex items-center gap-2 px-3 sm:px-4 py-1.5 sm:py-2 rounded-full bg-white/10 backdrop-blur-xl border border-pink-brand/30 dark:border-amber-500/30 mb-3 sm:mb-4 transition-all duration-500 hover:bg-white/20 hover:border-pink-brand/50 dark:hover:border-amber-500/50 color-transition-slow">
            <Icon name="ChatBubbleLeftRightIcon" size={16} variant="solid" className="text-pink-brand dark:text-amber-400" />
            <span className="text-xs sm:text-sm font-medium text-white/90">Patient Stories</span>
          </div>
          <h2 className="text-3xl sm:text-4xl md:text-5xl font-display font-bold text-white mb-3 sm:mb-4 px-4">
            Trusted by Women Across <span className="gradient-text-gold">Northern Uganda</span>
          </h2>
          <p className="text-base sm:text-lg text-white/80 max-w-2xl mx-auto px-4">
            Real stories from real patients who found compassionate, expert care at The Gyn Consult
          </p>
        </div>

        {/* Carousel Container */}
        <div className={`relative transition-all duration-1000 delay-300 gpu-accelerated ${
        isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-12"}`
        }>
          {/* Main Testimonial Card */}
          <div className="glass-dark rounded-2xl sm:rounded-3xl p-6 sm:p-8 md:p-12 shadow-2xl transition-all duration-700 hover:shadow-pink-brand/20 dark:hover:shadow-amber-500/20 hover:shadow-3xl gpu-accelerated dark:bg-navy-800/80 dark:border dark:border-amber-500/20">
            <div className="grid md:grid-cols-1 gap-6 sm:gap-8 items-center">
              {/* Profile Section */}
              <div className="flex flex-col items-center text-center">
                <h3 className="text-xl sm:text-2xl font-display font-bold text-white mb-1 transition-colors duration-500 color-transition">
                  {currentTestimonial.name}
                </h3>
                <p className="text-pink-brand text-xs sm:text-sm mb-3 sm:mb-4 flex items-center gap-1 transition-all duration-500 color-transition">
                  <Icon name="MapPinIcon" size={14} variant="solid" />
                  {currentTestimonial.location}
                </p>
                <div className="flex gap-1 mb-3 sm:mb-4">
                  {[...Array(currentTestimonial.rating)].map((_, i) =>
                  <Icon key={i} name="StarIcon" size={16} variant="solid" className="text-pink-brand transition-transform duration-300 md:hover:scale-125 hover:scale-110 gpu-accelerated sm:w-[18px] sm:h-[18px]" />
                  )}
                </div>
                <div className="px-3 sm:px-4 py-1.5 sm:py-2 rounded-full bg-white/10 border border-pink-brand/30 dark:border-amber-500/30 text-xs font-medium text-pink-brand dark:text-amber-400 transition-all duration-500 hover:bg-pink-brand/20 dark:hover:bg-amber-500/20 hover:border-pink-brand/50 dark:hover:border-amber-500/50 color-transition-slow">
                  {currentTestimonial.service}
                </div>
              </div>

              {/* Quote Section */}
              <div className="flex flex-col justify-center">
                <Icon name="ChatBubbleBottomCenterTextIcon" size={40} variant="solid" className="text-pink-brand/30 dark:text-amber-500/30 mb-4 sm:mb-6 transition-all duration-700 md:hover:text-pink-brand/50 dark:md:hover:text-amber-500/50 md:hover:scale-110 hover:text-pink-brand/40 dark:hover:text-amber-500/40 hover:scale-105 gpu-accelerated sm:w-12 sm:h-12" />
                <blockquote className="text-base sm:text-lg md:text-xl lg:text-2xl font-display text-white/95 leading-relaxed mb-6 sm:mb-8 transition-all duration-700">
                  "{currentTestimonial.quote}"
                </blockquote>
                
                {/* Trust Markers */}
                <div className="grid grid-cols-3 gap-3 sm:gap-4 pt-4 sm:pt-6 border-t border-white/10 dark:border-amber-500/20">
                  <div className="text-center transition-all duration-500 md:hover:scale-110 hover:scale-105 gpu-accelerated">
                    <div className="text-2xl sm:text-3xl font-bold text-pink-brand dark:text-amber-400 mb-1 transition-all duration-500 color-transition">500+</div>
                    <div className="text-[10px] sm:text-xs text-white/70">Happy Patients</div>
                  </div>
                  <div className="text-center transition-all duration-500 md:hover:scale-110 hover:scale-105 gpu-accelerated">
                    <div className="text-2xl sm:text-3xl font-bold text-pink-brand dark:text-amber-400 mb-1 transition-all duration-500 color-transition">15+</div>
                    <div className="text-[10px] sm:text-xs text-white/70">Years Experience</div>
                  </div>
                  <div className="text-center transition-all duration-500 md:hover:scale-110 hover:scale-105 gpu-accelerated">
                    <div className="text-2xl sm:text-3xl font-bold text-pink-brand dark:text-amber-400 mb-1 transition-all duration-500 color-transition">98%</div>
                    <div className="text-[10px] sm:text-xs text-white/70">Satisfaction Rate</div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Navigation Controls */}
          <div className="flex items-center justify-center gap-4 sm:gap-4 mt-8 sm:mt-8">
            <button
              onClick={goToPrevious}
              className="w-12 h-12 sm:w-12 sm:h-12 rounded-full bg-white/10 backdrop-blur-xl border border-pink-brand/20 dark:border-amber-500/20 hover:bg-pink-brand/20 dark:hover:bg-amber-500/20 hover:border-pink-brand/40 dark:hover:border-amber-500/40 transition-all duration-500 flex items-center justify-center group active:scale-90 md:hover:scale-110 hover:scale-105 gpu-accelerated min-h-[44px] min-w-[44px] touch-manipulation"
              aria-label="Previous testimonial">
              <Icon name="ChevronLeftIcon" size={20} variant="solid" className="text-white group-hover:text-pink-brand dark:group-hover:text-amber-400 transition-all duration-300 group-hover:-translate-x-1 color-transition w-5 h-5 sm:w-5 sm:h-5" />
            </button>

            {/* Dots Indicator */}
            <div className="flex gap-2 sm:gap-2">
              {testimonials.map((_, index) =>
              <button
                key={index}
                onClick={() => goToSlide(index)}
                className={`transition-all duration-500 rounded-full md:hover:scale-125 hover:scale-110 active:scale-90 gpu-accelerated min-h-[44px] min-w-[44px] flex items-center justify-center touch-manipulation ${
                index === currentIndex ?
                "w-10 sm:w-10 h-3 sm:h-3 bg-pink-brand dark:bg-amber-400 shadow-lg shadow-pink-brand/50 dark:shadow-amber-400/50" :
                "w-3 sm:w-3 h-3 sm:h-3 bg-white/30 hover:bg-pink-brand/50 dark:hover:bg-amber-400/50"}`
                }
                aria-label={`Go to testimonial ${index + 1}`}
                aria-current={index === currentIndex ? "true" : "false"} />

              )}
            </div>

            <button
              onClick={goToNext}
              className="w-12 h-12 sm:w-12 sm:h-12 rounded-full bg-white/10 backdrop-blur-xl border border-pink-brand/20 dark:border-amber-500/20 hover:bg-pink-brand/20 dark:hover:bg-amber-500/20 hover:border-pink-brand/40 dark:hover:border-amber-500/40 transition-all duration-500 flex items-center justify-center group active:scale-90 md:hover:scale-110 hover:scale-105 gpu-accelerated min-h-[44px] min-w-[44px] touch-manipulation"
              aria-label="Next testimonial">
              <Icon name="ChevronRightIcon" size={20} variant="solid" className="text-white group-hover:text-pink-brand dark:group-hover:text-amber-400 transition-all duration-300 group-hover:translate-x-1 color-transition w-5 h-5 sm:w-5 sm:h-5" />
            </button>
          </div>

          {/* Auto-play indicator */}
          <div className="text-center mt-4">
            <button
              onClick={() => setIsAutoPlaying(!isAutoPlaying)}
              className="text-xs text-white/60 hover:text-pink-brand dark:hover:text-amber-400 transition-all duration-300 flex items-center gap-2 mx-auto md:hover:scale-110 hover:scale-105 active:scale-95 color-transition gpu-accelerated min-h-[44px] px-4 touch-manipulation">
              {isAutoPlaying ?
              <>
                  <Icon name="PauseIcon" size={12} variant="solid" />
                  Auto-playing
                </> :
              <>
                  <Icon name="PlayIcon" size={12} variant="solid" />
                  Paused
                </>
              }
            </button>
          </div>
        </div>
      </div>
    </section>
  );
}